import { useCallback, useState } from 'react';

const STORAGE_KEY = 'jarvis.mediaLibrary.assetPopupSize';
const DEFAULT_W = 960;
const DEFAULT_H = 720;
const MIN_W = 480;
const MIN_H = 360;
const MAX_VIEWPORT_RATIO = 0.9;
const MAXIMIZE_RATIO = 0.98;

export interface MediaLibraryPopupSize {
  w: number;
  h: number;
}

function clamp(w: number, h: number, maxRatio = MAX_VIEWPORT_RATIO): MediaLibraryPopupSize {
  if (typeof window === 'undefined') {
    return { w: Number(w) || DEFAULT_W, h: Number(h) || DEFAULT_H };
  }
  const maxW = Math.floor(window.innerWidth * maxRatio);
  const maxH = Math.floor(window.innerHeight * maxRatio);
  return {
    w: Math.min(Math.max(MIN_W, Math.round(Number(w) || DEFAULT_W)), maxW),
    h: Math.min(Math.max(MIN_H, Math.round(Number(h) || DEFAULT_H)), maxH)
  };
}

function maximizedSize(): MediaLibraryPopupSize {
  if (typeof window === 'undefined') return { w: DEFAULT_W, h: DEFAULT_H };
  return {
    w: Math.floor(window.innerWidth * MAXIMIZE_RATIO),
    h: Math.floor(window.innerHeight * MAXIMIZE_RATIO)
  };
}

function readStoredSize(): MediaLibraryPopupSize {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clamp(DEFAULT_W, DEFAULT_H);
    const parsed = JSON.parse(raw) as { width?: number; height?: number };
    return clamp(parsed.width ?? DEFAULT_W, parsed.height ?? DEFAULT_H);
  } catch {
    return clamp(DEFAULT_W, DEFAULT_H);
  }
}

function persistSize(next: MediaLibraryPopupSize) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ width: next.w, height: next.h }));
  } catch {
    /* ignore quota */
  }
}

function parseDim(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseInt(value, 10);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function useMediaLibraryAssetPopupSize() {
  const [size, setSize] = useState<MediaLibraryPopupSize>(() => readStoredSize());
  const [isMaximized, setIsMaximized] = useState(false);

  const onResizeEnd = useCallback(
    (e: { component?: { option: (name: string) => unknown } }) => {
      if (isMaximized) return;
      const w = parseDim(e.component?.option('width'));
      const h = parseDim(e.component?.option('height'));
      if (w === undefined || h === undefined) return;
      const next = clamp(w, h);
      setSize(next);
      persistSize(next);
    },
    [isMaximized]
  );

  const maximize = useCallback(() => setIsMaximized(true), []);

  const restoreDefault = useCallback(() => {
    const next = clamp(DEFAULT_W, DEFAULT_H);
    setIsMaximized(false);
    setSize(next);
    persistSize(next);
  }, []);

  const display = isMaximized ? maximizedSize() : size;

  return {
    size: display,
    isMaximized,
    maximize,
    restoreDefault,
    onResizeEnd,
    maxWidth: isMaximized ? '98vw' : '90vw',
    maxHeight: isMaximized ? '98vh' : '90vh'
  };
}
